import { getCanonicalFieldKey, parseNum } from "./status";
import { RAW_MATCH_STATUSES, CELL_STATUSES } from "./constants";
import type { ControlEntry } from "./types";
import type { ReconciliationCellDto } from "../data/dto";

function toStr(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined) return undefined;
  return String(value);
}

export function buildControlsMap(
  rows: ReconciliationCellDto[],
  selectedFundIds: number[],
): Record<string, ControlEntry> {
  const map: Record<string, ControlEntry> = {};
  const wantedFundIds = new Set(selectedFundIds);

  for (const row of rows) {
    const level =
      row.level === "fund" || row.level === "company_numeric"
        ? row.level
        : undefined;
    const fieldName = getCanonicalFieldKey(
      row.field_key_canonical ?? row.field_name,
    );
    const status = String(row.reconciliation_status ?? "").toLowerCase();
    const fundId = parseNum(row.fund_id);
    const issuerId = parseNum(row.issuer_id);
    if (!fundId || !fieldName) continue;
    if (wantedFundIds.size > 0 && !wantedFundIds.has(fundId)) continue;
    const key = String(row.cell_key ?? "").trim();
    if (!key) continue;

    const rawMatchStatus = row.raw_match_status;
    const cellStatus = row.cell_status;
    const deltaTone = row.delta_tone;
    const fieldGroup = row.field_group;
    const entityType = row.entity_type;

    map[key] = {
      id: row.id != null ? Number(row.id) : null,
      cellKey: key,
      entityType:
        entityType === "fund" ||
        entityType === "issuer" ||
        entityType === "position"
          ? entityType
          : undefined,
      fundId,
      issuerId,
      positionId: parseNum(row.position_id),
      status,
      businessKey: toStr(row.business_key),
      fieldName: toStr(row.field_name),
      sectionName: toStr(row.section_name),
      fundName: toStr(row.fund),
      issuerName: toStr(row.issuer),
      trrValue: toStr(row.trr_raw_value),
      pdfValue: toStr(row.pdf_raw_value),
      goldenValue: toStr(row.golden_value),
      materialityAbs: row.materiality_abs ?? null,
      reasonCode: toStr(row.reason_code),
      lastSeenAt: toStr(row.last_seen_at),
      rawMatchStatus:
        rawMatchStatus != null &&
        (RAW_MATCH_STATUSES as readonly string[]).includes(rawMatchStatus)
          ? (rawMatchStatus as ControlEntry["rawMatchStatus"])
          : undefined,
      cellStatus:
        cellStatus != null &&
        (CELL_STATUSES as readonly string[]).includes(cellStatus)
          ? (cellStatus as ControlEntry["cellStatus"])
          : undefined,
      colorCode: (() => {
        const VALID = [
          "violet",
          "orange_light",
          "green_light",
          "green_dark",
          "red",
          "neutral",
        ] as const;
        type C = (typeof VALID)[number];
        const fromDisplay = VALID.includes(row.display_color as C)
          ? (row.display_color as C)
          : undefined;
        const fromCode = VALID.includes(row.color_code as C)
          ? (row.color_code as C)
          : undefined;
        const resolved = fromDisplay ?? fromCode;
        if (resolved) return resolved;
        // Backend may omit display_color for false_positive / has_override — derive locally.
        if (
          String(row.reconciliation_status ?? "").toLowerCase() ===
          "false_positive"
        )
          return "green_dark" as C;
        if (row.has_override) return "violet" as C;
        return undefined;
      })(),
      colorReason: toStr(row.color_reason),
      hasOverride: Boolean(row.has_override),
      autoMatch: Boolean(row.auto_match),
      previousValue: toStr(row.previous_value),
      deltaLabel: toStr(row.delta_label),
      deltaTone:
        deltaTone === "success" ||
        deltaTone === "danger" ||
        deltaTone === "warning" ||
        deltaTone === "default"
          ? deltaTone
          : undefined,
      displayValue: toStr(row.display_value),
      level,
      fieldGroup:
        fieldGroup === "fund" ||
        fieldGroup === "company" ||
        fieldGroup === "numeric" ||
        fieldGroup === "other"
          ? fieldGroup
          : undefined,
      reportingDate: parseNum(row.reporting_date),
    };
  }

  return map;
}

export function inferPositionId(
  sectionName: string,
  positionId: number | null | undefined,
  fundId: number,
  issuerId: number | null | undefined,
  reportingDate: number | null | undefined,
  positionsByKey:
    | Map<string, { id?: number | null }>
    | Record<string, { id?: number | null }>,
): number | null {
  if (positionId != null) return positionId;
  if (sectionName !== "r1_raw_portfolio_positions") return null;
  if (issuerId == null || reportingDate == null) return null;
  const key = `f:${fundId}:i:${issuerId}:d:${reportingDate}`;
  const inferred =
    positionsByKey instanceof Map
      ? positionsByKey.get(key)
      : positionsByKey[key];
  return inferred?.id != null ? Number(inferred.id) : null;
}

export function buildSummary(apiSummary?: Record<string, unknown>) {
  const detectedMismatches = Number(apiSummary?.detected_mismatches ?? 0);
  const toReview = Number(apiSummary?.to_review ?? apiSummary?.unresolved ?? 0);
  return {
    detectedMismatches,
    toReview,
    otherOpenDiagnostics: Math.max(0, toReview - detectedMismatches),
    falsePositives: Number(apiSummary?.false_positives ?? 0),
    resolved: Number(apiSummary?.resolved ?? 0),
  };
}
